import {
  type PanelBankDiff,
  SectionPanelSorting,
  type UpdatePanel,
} from '../../../components/PanelBank/types';
import {
  addMetricsToPanelConfigImmutable,
  getDefaultPanelConfig,
  updateDefaultxAxisImmutable,
} from '../../../util/panelbank';
import {getDefaultPanelSectionConfig} from '../../../util/panelbankConfigs';
import {
  PANEL_BANK_CHARTS_NAME,
  PANEL_BANK_EVALUATION_NAME,
  PANEL_BANK_EVALUATION_TABLES_NAME,
} from '../../../util/panelbankTypes';
import * as Panels from '../../../util/panels';
import {type LayedOutPanel} from '../../../util/panelTypes';
import * as Normalize from '../normalize';
import {type StateType} from '../normalizerSupport';
import type * as PanelTypes from '../panel/types';
import type * as PanelBankSectionConfigTypes from '../panelBankSectionConfig/types';
import {immutableInsertPanelsAlphabetically} from '../panelBankSectionConfig/utils';
import type * as Types from './types';

interface SectionInfo {
  keyType: string; // this is needed if the section has to be created
  panelRefs: PanelTypes.Ref[];
}

export function collectExistingPanelsForDiff(
  panels: StateType['panel'],
  panelIdsToUpdate: ReadonlySet<string>,
  mergedPanelRegexes: RegExp[]
): {
  panelsById: Map<string, LayedOutPanel>;
  panelMapKeyById: Map<string, string>;
  panelIdsToRemove: Set<string>;
} {
  const panelsById = new Map<string, LayedOutPanel>();
  const panelMapKeyById = new Map<string, string>();
  const panelIdsToRemove = new Set<string>();

  if (panelIdsToUpdate.size === 0 && mergedPanelRegexes.length === 0) {
    return {panelsById, panelMapKeyById, panelIdsToRemove};
  }

  for (const mapKey of Object.keys(panels)) {
    const panel = panels[mapKey];
    if (panelIdsToUpdate.has(panel.__id__)) {
      panelsById.set(panel.__id__, panel);
      // panel.__id__ can diverge from the map key after cache restore and
      // re-normalization, so updates must retain the actual map key.
      panelMapKeyById.set(panel.__id__, mapKey);
    }

    if (
      mergedPanelRegexes.length &&
      panel.viewType === 'Run History Line Plot' &&
      panel.isAuto
    ) {
      const panelKey = Panels.getKey(panel);
      const shouldRemove =
        panelKey != null &&
        mergedPanelRegexes.some(regex => regex.test(panelKey));
      if (shouldRemove) {
        panelIdsToRemove.add(panel.__id__);
      }
    }
  }

  return {panelsById, panelMapKeyById, panelIdsToRemove};
}

export const immutableAddOrUpdatePanelsToSections = (
  panelBankDiff: PanelBankDiff,
  reducerStateParts: StateType,
  panelBankConfigRef: Types.Ref,
  isAuto: boolean,
  shouldAlphaInsert = false
): StateType => {
  // Optimization: since new panels are placed at the beginning of the section
  // (for non-alphabetized sections), we create the full list of metrics
  // to add (grouped by section name in this map), then add all the
  // metrics for each section in one go.
  //
  // If you change this function or its children, be aware it can be
  // run with up to 100k of metrics to add to a section or sections.
  const newPanelsBySection = new Map<
    string, // section name
    SectionInfo
  >();

  // Track panels to remove for merged regex panels
  const panelIdsToUpdate = new Set<string>();
  const mergedPanelRegexes = panelBankDiff.reduce((acc, {operation}) => {
    if (operation.type === 'update') {
      panelIdsToUpdate.add(operation.panelId);
    }
    const {spec} = operation;
    if (spec.type === 'regex-panel' && spec.isMergedPanel) {
      acc.push(new RegExp(spec.metricRegex));
    }
    return acc;
  }, [] as RegExp[]);
  const {panelsById, panelMapKeyById, panelIdsToRemove} =
    collectExistingPanelsForDiff(
      reducerStateParts.panel,
      panelIdsToUpdate,
      mergedPanelRegexes
    );

  const sectionInfos = [];
  const defaultConfigs = [];
  const updatedPanels: {[id: string]: LayedOutPanel} = {};

  for (const {key, operation} of panelBankDiff) {
    if (operation.type === 'update') {
      const previouslyUpdatedPanel = updatedPanels[operation.panelId];
      const panel = previouslyUpdatedPanel ?? panelsById.get(operation.panelId);
      if (panel) {
        const updatedPanel = updatePanelImmutable(operation, panel);
        updatedPanels[operation.panelId] = updatedPanel;
      }
    } else if (operation.type === 'add') {
      // this is either a new panel, or a just-deleted panel we need to add
      // to the "Hidden" section
      const targetSectionName = operation.spec.defaultSection;
      let sectionInfo = newPanelsBySection.get(targetSectionName);
      if (sectionInfo == null) {
        sectionInfo = {keyType: operation.spec.keyType ?? '', panelRefs: []};
        newPanelsBySection.set(targetSectionName, sectionInfo);
      }
      sectionInfos.push(sectionInfo);
      const panel = getDefaultPanelConfig(key, operation.spec, isAuto);
      defaultConfigs.push(panel);
    }
  }

  const {parts: partsWithNewPanels, refs: newPanelRefs} =
    Normalize.addObjsImmutable(
      reducerStateParts,
      'panel',
      panelBankConfigRef.viewID,
      defaultConfigs
    );

  if (Object.keys(updatedPanels).length > 0) {
    // Clone the panel map so Redux detects the state change.
    if (partsWithNewPanels.panel === reducerStateParts.panel) {
      partsWithNewPanels.panel = {...partsWithNewPanels.panel};
    }
    Object.entries(updatedPanels).forEach(([id, panel]) => {
      // Use the actual map key from parts.panel, not panel.__id__, because
      // they can diverge after cache restore + re-normalization.
      const actualMapKey = panelMapKeyById.get(id) ?? id;
      partsWithNewPanels.panel[actualMapKey] = panel;
    });
  }

  sectionInfos.forEach((sectionInfo, index) => {
    sectionInfo.panelRefs.push(newPanelRefs[index]);
  });

  // Now that we've grouped all the metrics by section,
  // add the metric list to each section
  const partsWithNewSections = immutableAddAllSectionInfosToReducerState(
    newPanelsBySection,
    partsWithNewPanels,
    panelBankConfigRef,
    isAuto,
    shouldAlphaInsert
  );

  // Remove any panels that are now captured by merged regex panels
  const partsWithRemovedPanels = removePanelsFromPanelBank(
    partsWithNewSections,
    panelBankConfigRef,
    panelIdsToRemove
  );

  return partsWithRemovedPanels;
};

// this takes the new panels we need to add (grouped by section)
// and adds all the panels to each section
function immutableAddAllSectionInfosToReducerState(
  newPanelsBySection: Map<string, SectionInfo>,
  prevReducerStateParts: StateType,
  panelBankConfigRef: Types.Ref,
  isAuto: boolean,
  shouldAlphaInsert = false
): StateType {
  if (!newPanelsBySection.size) {
    return prevReducerStateParts;
  }

  const prevSectionRefs =
    prevReducerStateParts[panelBankConfigRef.type][panelBankConfigRef.id]
      .sectionRefs;

  // Create map of section refs for quick lookup. Sections are indexed by both
  // their current name AND their defaultName (if set). This allows renamed
  // sections to still match during decompression when auto-generated panels
  // look for the original section name (e.g., "Charts").
  const sectionRefsMap = new Map<string, PanelBankSectionConfigTypes.Ref>();
  prevSectionRefs.forEach(sectionRef => {
    const section = prevReducerStateParts[sectionRef.type][sectionRef.id];
    sectionRefsMap.set(section.name.toUpperCase(), sectionRef);
    if (section.defaultName && section.defaultName !== section.name) {
      sectionRefsMap.set(section.defaultName.toUpperCase(), sectionRef);
    }
  });

  const newSectionsNames: string[] = [];
  const newSectionsInfos: SectionInfo[] = [];

  const targetSectionRefs: PanelBankSectionConfigTypes.Ref[] = Array(
    newPanelsBySection.size
  ).fill(null);

  const newSectionsIndices: number[] = [];
  const sectionInfos: SectionInfo[] = [];

  let i = 0;
  for (const [sectionName, sectionInfo] of newPanelsBySection) {
    const targetSectionRef = sectionRefsMap.get(sectionName.toUpperCase());
    sectionInfos.push(sectionInfo);

    if (!targetSectionRef) {
      newSectionsNames.push(sectionName);
      newSectionsInfos.push(sectionInfo);
      newSectionsIndices.push(i);
    } else {
      targetSectionRefs[i] = targetSectionRef;
    }
    i++;
  }

  const {newParts, refs: newSectionRefs} = immutableCreateNewSections(
    newSectionsNames,
    newSectionsInfos,
    prevReducerStateParts,
    panelBankConfigRef,
    isAuto
  );

  newSectionRefs.forEach((ref, index) => {
    targetSectionRefs[newSectionsIndices[index]] = ref;
  });

  const newSectionConfigParts = {
    ...newParts['panel-bank-section-config'],
  };

  targetSectionRefs.forEach((targetSectionRef, index) => {
    const sectionInfo = sectionInfos[index];

    // now that we're guaranteed to have a ref for the target section,
    // we can resolve it:
    let targetSection = newParts[targetSectionRef.type][targetSectionRef.id];

    // sections with manually added panels should be marked `isPanelsAuto: false`
    if (isAuto === false && targetSection.isPanelsAuto !== false) {
      targetSection = {
        ...targetSection,
        isPanelsAuto: false,
      };
    }

    if (
      shouldAlphaInsert ||
      targetSection.sorted === SectionPanelSorting.Alphabetical
    ) {
      targetSection = immutableInsertPanelsAlphabetically(
        newParts,
        targetSection,
        sectionInfo.panelRefs
      );
      newSectionConfigParts[targetSectionRef.id] = targetSection;
    } else {
      newSectionConfigParts[targetSectionRef.id] = {
        ...targetSection,
        panelRefs: [
          ...sectionInfo.panelRefs.reverse(),
          ...targetSection.panelRefs,
        ],
      };
    }
  });

  newParts['panel-bank-section-config'] = newSectionConfigParts;

  return newParts;
}

// create a new PanelSectionConfig
function immutableCreateNewSections(
  sectionNames: string[],
  sectionInfos: SectionInfo[],
  prevReducerStateParts: StateType,
  ref: Types.Ref,
  isAuto: boolean // whether sections are being added as part of auto panel gen
): {newParts: StateType; refs: PanelBankSectionConfigTypes.Ref[]} {
  const defaultConfigs = [];
  for (let i = 0; i < sectionNames.length; i++) {
    const sectionName = sectionNames[i];
    const defaultConfig = getDefaultPanelSectionConfig({
      name: sectionName,
    });
    defaultConfig.isPanelsAuto = isAuto;
    if (isAuto) {
      defaultConfig.defaultName = sectionName;
    }
    defaultConfigs.push(defaultConfig);
  }

  const {parts: newParts, refs} = Normalize.addObjsImmutable(
    prevReducerStateParts,
    'panel-bank-section-config',
    ref.viewID,
    defaultConfigs
  );

  let newSectionRefs = [...newParts[ref.type][ref.id].sectionRefs];

  refs.forEach((targetSectionRef, index) => {
    const firstSectionRef = newSectionRefs[0];

    const firstSection = firstSectionRef
      ? newParts[firstSectionRef.type][firstSectionRef.id]
      : undefined;

    const sectionName = sectionNames[index];
    if (sectionName === 'System') {
      // "System" section goes towards the bottom, above only "Hidden"
      newSectionRefs.splice(newSectionRefs.length - 1, 0, targetSectionRef);
    } else if (firstSection && firstSection.name === 'Sweep') {
      // "Sweep" section should remain at the top; insert at index 1
      newSectionRefs.splice(1, 0, targetSectionRef);
    } else if (
      sectionName === PANEL_BANK_EVALUATION_NAME ||
      sectionName === PANEL_BANK_EVALUATION_TABLES_NAME
    ) {
      // Order eval tables after charts and before weave evals
      const anchorName =
        sectionName === PANEL_BANK_EVALUATION_NAME &&
        newSectionRefs.some(
          r => newParts[r.type][r.id].name === PANEL_BANK_EVALUATION_TABLES_NAME
        )
          ? PANEL_BANK_EVALUATION_TABLES_NAME
          : PANEL_BANK_CHARTS_NAME;
      const anchorIndex = newSectionRefs.findIndex(
        ref => newParts[ref.type][ref.id].name === anchorName
      );
      if (anchorIndex !== -1) {
        newSectionRefs.splice(anchorIndex + 1, 0, targetSectionRef);
      } else {
        // If anchor doesn't exist, add to top as fallback
        newSectionRefs = [targetSectionRef].concat(newSectionRefs);
      }
    } else {
      // Otherwise just add new section to the top -- in this case `concat`
      // is more performant than `splice` or `unshift`. This matters because
      // some projects could have tooooons of sections (like 25k)
      newSectionRefs = [targetSectionRef].concat(newSectionRefs);
    }
  });

  newParts[ref.type] = {
    ...newParts[ref.type],
    [ref.id]: {
      ...newParts[ref.type][ref.id],
      sectionRefs: newSectionRefs,
    },
  };

  return {newParts, refs};
}

// updates the config for a particular panel
function updatePanelImmutable(
  operation: UpdatePanel,
  existingPanel: LayedOutPanel
): LayedOutPanel {
  if (
    operation.spec.type === 'legacy-vega' ||
    operation.spec.type === 'api-added-panel'
  ) {
    return {
      ...existingPanel,
      viewType:
        operation.spec.type === 'legacy-vega'
          ? 'Vega'
          : (operation.spec.viewType ?? existingPanel.viewType),
      config: operation.spec.config,
    } as LayedOutPanel;
  } else if (
    existingPanel.viewType === 'Run History Line Plot' &&
    operation.spec.type === 'default-panel'
  ) {
    let newPanel = addMetricsToPanelConfigImmutable(
      existingPanel,
      operation.spec.metrics
    );
    newPanel = updateDefaultxAxisImmutable(
      newPanel,
      operation.spec.defaultXAxis
    );
    return newPanel;
  }
  return existingPanel;
}

/**
 * Given a set of panel IDs, remove those panels from the sections they are in.
 * If all panels are removed from a section, remove the entire section from the panel bank.
 *
 * This does NOT actually delete any panel or section parts from the reducer state.
 * Instead, we simply filter them out of section.panelRefs or pbConfig.sectionRefs.
 * Keeping the panel/section parts around allows us to easily revert on undo.
 */
function removePanelsFromPanelBank(
  parts: StateType,
  panelBankConfigRef: Types.Ref,
  panelIdsToRemove: Set<string>
): StateType {
  if (!panelIdsToRemove.size) {
    return parts;
  }

  const newParts = {...parts};
  const sectionRefsToRemove = new Set<PanelBankSectionConfigTypes.Ref>();
  const panelBankConfig = parts[panelBankConfigRef.type][panelBankConfigRef.id];

  for (const sectionRef of panelBankConfig.sectionRefs) {
    const section = parts[sectionRef.type][sectionRef.id];
    const filteredPanelRefs = section.panelRefs.filter(panelRef => {
      const panelId = newParts[panelRef.type][panelRef.id].__id__;
      return !panelIdsToRemove.has(panelId);
    });

    // Only update if there were actual removals in this section
    if (filteredPanelRefs.length !== section.panelRefs.length) {
      // Mark entire section for removal if no panels remain
      if (filteredPanelRefs.length === 0) {
        sectionRefsToRemove.add(sectionRef);
      } else {
        newParts[sectionRef.type][sectionRef.id] = {
          ...section,
          panelRefs: filteredPanelRefs,
        };
      }
    }
  }

  // Remove sections that are now empty
  if (sectionRefsToRemove.size) {
    newParts[panelBankConfigRef.type][panelBankConfigRef.id] = {
      ...panelBankConfig,
      sectionRefs: panelBankConfig.sectionRefs.filter(
        // note: reference equality works here, but that's not always the case
        // when comparing refs. just be careful/keep in mind for other use cases
        ref => !sectionRefsToRemove.has(ref)
      ),
    };
  }

  return newParts;
}
